import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperClass } from "swiper";
import { EffectFade } from "swiper/modules";

import "swiper/css";
import "swiper/css/effect-fade";

import { formatLikeCount, movieTitleHandler } from "../../../utils/helpers";
import { ROUTES } from "../../../constants/routes";
import { genresData } from "../../../constants/structure";
import SavedIcon from "../../../components/icons/searchIcons/savedIcon";
import OverlayRatingIcon from "../../../components/icons/searchIcons/OverlayRatingIcon";
import OverlayTomatoRatingIcon from "../../../components/icons/searchIcons/OverlayTomatoRatingIcon";
import LikeIcon from "../../../components/icons/chat/likeIcon";
import ShareIcon from "../../../components/icons/chat/shareIcon";
import BookMarkIcon from "../../../components/icons/chat/bookMarkIcon";
import LikeButton from "../../../components/shared/LikeButton";
import styles from "./MovieCarousel.module.scss";
import RoomVideoPlayer from "../../room/components/RoomVideoPlayer";

const formatRating = (rating?: number | null) => (rating != null ? rating.toFixed(1) : undefined);

interface Movie {
  id: number;
  title: string;
  title_ru: string;
  title_ua: string;
  title_ar: string;
  poster_path: string;
  backdrop_path: string;
  trailer_key?: string | null;
  release_date: string;
  vote_average: number;
  vote_count: number;
  imdb_rating?: number | null;
  rotten_tomatoes_rating?: number | null;
  genre_ids: number[];
  overview: string;
  overview_ru: string;
  overview_ua: string;
  liked: boolean;
  in_favorite: boolean;
  shares_count: number;
  favorites_count: number;
}

interface CarouselProps {
  movies: Movie[];
  muted: boolean;
  onToggleMuted: () => void;
  likeHandler: (e: any, item: any, liked: boolean) => void;
  favouritesHandler: (e: any, item: any, isFav: boolean) => void;
  likesList: number[];
  favoritesList: number[];
  updateVoteLocal: (id: any, key: string, type: string) => void;
  isMobile: boolean;
  openLoginModal: () => void;
  onShare: (id: number) => void;
}

const IMG_BASE = process.env.REACT_APP_IMG_BASE_URL;

const MovieCarouselDesktop: React.FC<CarouselProps> = ({
                                                         movies,
                                                         muted,
                                                         onToggleMuted,
                                                         isMobile,
                                                         likeHandler,
                                                         favouritesHandler,
                                                         likesList,
                                                         favoritesList,
                                                         updateVoteLocal,
                                                         openLoginModal,
                                                         onShare,
                                                       }) => {
  const { i18n, t } = useTranslation();
  const dirData = i18n.language === "ar" ? "rtl" : "ltr";
  const navigate = useNavigate();

  const [activeIndex, setActiveIndex] = useState(0);
  const mainSwiperRef = useRef<SwiperClass | null>(null);
  const thumbsSwiperRef = useRef<SwiperClass | null>(null);

  const syncSliders = (index: number) => {
    setActiveIndex(index);
    if (mainSwiperRef.current && mainSwiperRef.current.activeIndex !== index) {
      mainSwiperRef.current.slideTo(index);
    }
    if (thumbsSwiperRef.current && thumbsSwiperRef.current.activeIndex !== index) {
      thumbsSwiperRef.current.slideTo(index);
    }
  };

  const handleNavigateUp = () => {
    if (activeIndex > 0) {
      syncSliders(activeIndex - 1);
    }
  };

  const handleNavigateDown = () => {
    if (activeIndex < movies.length - 1) {
      syncSliders(activeIndex + 1);
    }
  };

  const rightSliderContainerRef = useRef<HTMLDivElement | null>(null);

  const updateOffsetAfter = () => {
    const swiper = thumbsSwiperRef.current;
    const containerEl = rightSliderContainerRef.current;
    if (!swiper || !containerEl) return;

    const containerWidth = containerEl.offsetWidth;
    const firstSlideEl = swiper.slides?.[0] as HTMLElement | undefined;
    const slideWidth = firstSlideEl ? firstSlideEl.offsetWidth : 0;

    const offsetAfter = Math.max(containerWidth - slideWidth, 0);

    swiper.params.slidesOffsetAfter = offsetAfter;
    swiper.update();
  };

  useEffect(() => {
    updateOffsetAfter();
    window.addEventListener("resize", updateOffsetAfter);
    return () => window.removeEventListener("resize", updateOffsetAfter);
  }, [movies.length]);

  return (
    <div className={styles.movieCarouselContainer}>
      <div className={styles.movieCarouselMainGrid}>

        <div className={styles.leftMoviesSlider}>
          <Swiper
            modules={[EffectFade]}
            effect={"fade"}
            fadeEffect={{ crossFade: true }}
            onSwiper={(swiper: SwiperClass) => { mainSwiperRef.current = swiper; }}
            onSlideChange={(swiper: SwiperClass) => syncSliders(swiper.activeIndex)}
            allowTouchMove={isMobile}
            className={styles.mainMoviesSlider}
          >
            {movies.map((movie, index) => {
              const title = movieTitleHandler(i18n.language, movie) || movie.title;
              const year = movie.release_date?.substring(0, 4);
              const genres = movie.genre_ids
                ?.slice(0, 2)
                .map((gid) => genresData.find((g) => g.id === gid)?.name)
                .filter(Boolean)
                .map((name) => t(name!))
                .join(", ");

              const primaryRating = formatRating(movie.imdb_rating ?? movie.vote_average) || "—";
              const secondaryRating = formatRating(movie.rotten_tomatoes_rating ?? movie.imdb_rating ?? movie.vote_average) || primaryRating;
              const liked = likesList?.includes(movie.id);
              const isFav = favoritesList?.includes(movie.id);

              return (
                <SwiperSlide key={movie.id} className={styles.mainMoviesSlide}>
                  <div className={styles.movieCarouselVideo}>
                    <RoomVideoPlayer
                      trailerKey={movie.trailer_key ?? null}
                      backdropPath={movie.backdrop_path}
                      muted={muted}
                      onToggleMuted={onToggleMuted}
                      isActive={activeIndex === index}
                      navigation={{
                        mode: "slides",
                        onNavigateUp: handleNavigateUp,
                        onNavigateDown: handleNavigateDown,
                        canNavigateUp: activeIndex > 0,
                        canNavigateDown: activeIndex < movies.length - 1,
                      }}
                    />
                  </div>

                  <div className={styles.movieCarouselInfo}>
                    <div className={styles.movieCarouselBlock}>
                      {movie.poster_path ? (
                        <div className={styles.movieCarouselImage}>
                          <img src={`${IMG_BASE}${movie.poster_path}`} alt={title} />
                        </div>
                      ) : (
                        <div className={styles.movieCarouselPosterPlaceholderSmall} />
                      )}

                      <div className={styles.movieCarouselBox}>
                        <h4 className={styles.movieCarouselSlideTitle}>{title}</h4>
                        <div className={styles.movieCarouselSlideMeta}>
                          {year && <span className={styles.year}>{year}</span>}
                          {genres && <span className={styles.genres}>{genres}</span>}
                        </div>
                        <div className={styles.ratings}>
                          <div className={styles.ratingItem}>
                            <OverlayRatingIcon />
                            <span className={styles.rating}>{primaryRating}</span>
                          </div>
                          <div className={styles.ratingItem}>
                            <OverlayTomatoRatingIcon />
                            <span className={styles.rating}>{secondaryRating}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={styles.movieCarouselActions}>

                      <div
                        className={styles.movieCarouselActionsItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          const token = localStorage.getItem("accessToken");
                          if (!token) return openLoginModal();
                          likeHandler(e, movie, liked);
                        }}
                      >
                        <div className={`${styles.movieCarouselActionsIcon} ${liked ? styles.movieCarouselActionsIconActive : ""}`}><LikeIcon/></div>
                        <div className={styles.movieCarouselActionsValue}>{formatLikeCount(movie.vote_count, { compact: true })}</div>
                      </div>

                      <div
                        className={styles.movieCarouselActionsItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          const token = localStorage.getItem("accessToken");
                          if (!token) return openLoginModal();
                          favouritesHandler(e, movie, isFav);
                        }}
                      >
                        <div className={`${styles.movieCarouselActionsIcon} ${isFav ? styles.movieCarouselActionsIconActive : ""}`}><BookMarkIcon/></div>
                        <div className={styles.movieCarouselActionsValue}>{formatLikeCount(movie.favorites_count, { compact: true })}</div>
                      </div>

                      <div
                        className={styles.movieCarouselActionsItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          onShare(movie.id);
                        }}
                      >
                        <div className={styles.movieCarouselActionsIcon}><ShareIcon /></div>
                        <div className={styles.movieCarouselActionsValue}>{formatLikeCount(movie.shares_count, { compact: true })}</div>
                      </div>
                    </div>

                    <div className={styles.movieCarouselButtons}>
                      <button
                        className={styles.movieCarouselButton}
                        onClick={() => navigate(`${ROUTES.DETAILS}/${movie.id}`)}
                      >
                        <span>{t("room_details", "Details")}</span>
                      </button>
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>

        </div>

        <div className={styles.rightMoviesSlider} ref={rightSliderContainerRef}>
          <Swiper
            onSwiper={(swiper: SwiperClass) => {
              thumbsSwiperRef.current = swiper;
              requestAnimationFrame(updateOffsetAfter);
            }}
            onSlideChange={(swiper: SwiperClass) => syncSliders(swiper.activeIndex)}
            slidesPerView={"auto"}
            spaceBetween={20}
            centeredSlides={false}
            slidesOffsetBefore={0}
            className={styles.thumbsMoviesSlider}
          >
            {movies.map((movie, i) => {
              const title = movieTitleHandler(i18n.language, movie) || movie.title;
              const year = movie.release_date?.substring(0, 4);
              const genres = movie.genre_ids
                ?.slice(0, 2)
                .map((gid) => genresData.find((g) => g.id === gid)?.name)
                .filter(Boolean)
                .map((name) => t(name!))
                .join(", ");

              const isCurrent = i === activeIndex;
              const primaryRating = formatRating(movie.imdb_rating ?? movie.vote_average) || "—";
              const secondaryRating = formatRating(movie.rotten_tomatoes_rating ?? movie.imdb_rating ?? movie.vote_average) || primaryRating;
              const liked = likesList?.includes(movie.id);
              const isFav = favoritesList?.includes(movie.id);


              return (
                <SwiperSlide
                  key={movie.id}
                  className={`${styles.movieCarouselSlide} ${isCurrent ? styles.thumbActiveHidden : ""}`}
                  onClick={() => syncSliders(i)}
                >
                  <div className={styles.movieCarouselCard}>
                    {movie.poster_path ? (
                      <div className={styles.movieCarouselPoster}>
                        <img src={`${IMG_BASE}${movie.poster_path}`} alt={title}/>
                      </div>
                    ) : (
                      <div className={styles.movieCarouselPosterPlaceholder}/>
                    )}

                    <div className={styles.movieCarouselSlideArea}>
                      <div className={styles.movieCarouselHeading}>
                        <h4 className={styles.movieCarouselSlideTitle}>{title}</h4>
                        <div
                          dir={dirData}
                          className={styles.savedIconWrapper}
                          onClick={(e: any) => {
                            e.stopPropagation();
                            const token = localStorage.getItem("accessToken");
                            if (!token) return openLoginModal();
                            favouritesHandler(e, movie, isFav);
                          }}
                        >
                          <SavedIcon className={`${isMobile ? styles.savedIcon : styles.savedIconDesctop}`} inFavourites={isFav} />
                        </div>
                      </div>

                      {year && (
                        <div className={styles.movieCarouselDate}>
                          <span className={styles.yearBadge}>{year}</span>
                        </div>
                      )}

                      {genres && (
                        <div className={styles.movieCarouselGenres}>
                          <span className={styles.genres}>{genres}</span>
                        </div>
                      )}

                      <div className={styles.ratingsBlock}>
                        <div className={styles.ratingItem}>
                          <OverlayRatingIcon />
                          <span className={styles.rating}>{primaryRating}</span>
                        </div>
                        <div className={styles.ratingItem}>
                          <OverlayTomatoRatingIcon />
                          <span className={styles.rating}>{secondaryRating}</span>
                        </div>
                        <LikeButton
                          clickHandler={likeHandler}
                          direction="row"
                          isMobile={false}
                          className={styles.likeButton}
                          count={movie.vote_count}
                          updateFieldLocal={updateVoteLocal}
                          liked={liked}
                          item={movie}
                        />
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>

      </div>

      <div className={styles.movieCarouselNav}>
        {movies.map((_, i) => (
          <button
            key={i}
            className={`${styles.movieCarouselDot} ${i === activeIndex ? styles.movieCarouselDotActive : ""}`}
            onClick={() => syncSliders(i)}
          />
        ))}
      </div>
    </div>
  );
};

export default MovieCarouselDesktop;
